'use client';

import { motion } from 'framer-motion';

interface BlackHoleLoaderProps {
	size?: 'sm' | 'md' | 'lg';
	className?: string;
}

const sizes = {
	sm: 'w-16 h-16',
	md: 'w-24 h-24',
	lg: 'w-32 h-32',
};

export function BlackHoleLoader({ size = 'md', className = '' }: BlackHoleLoaderProps) {
	return (
		<div className={`relative ${sizes[size]} ${className}`}>
			{/* Outer glow */}
			<motion.div
				className="absolute inset-0 rounded-full bg-matrix-400/20 blur-xl"
				animate={{
					scale: [1, 1.2, 1],
					opacity: [0.5, 0.8, 0.5],
				}}
				transition={{
					duration: 2,
					repeat: Infinity,
					ease: 'easeInOut',
				}}
			/>

			{/* Black hole ring */}
			<motion.div
				className="absolute inset-0 rounded-full border-2 border-matrix-400"
				animate={{
					rotate: 360,
					scale: [1, 0.8, 1],
				}}
				transition={{
					duration: 3,
					repeat: Infinity,
					ease: 'linear',
				}}
			/>

			{/* Inner spiral */}
			{[...Array(3)].map((_, i) => (
				<motion.div
					key={i}
					className="absolute inset-0 rounded-full border border-matrix-500/60"
					initial={{
						scale: 1,
						opacity: 1,
					}}
					animate={{
						scale: 0,
						opacity: 0,
					}}
					transition={{
						duration: 2,
						repeat: Infinity,
						delay: i * 0.3,
						ease: 'easeInOut',
					}}
				/>
			))}

			{/* Center black hole */}
			<motion.div
				className="bg-cyber-darker absolute inset-4 rounded-full shadow-lg"
				animate={{
					scale: [0.8, 1, 0.8],
				}}
				transition={{
					duration: 2,
					repeat: Infinity,
					ease: 'easeInOut',
				}}>
				<div className="absolute inset-0 bg-gradient-radial from-matrix-500/20 to-transparent opacity-50" />
			</motion.div>

			{/* Particles being sucked in */}
			{[...Array(8)].map((_, i) => (
				<motion.div
					key={`particle-${i}`}
					className="absolute h-1 w-1 rounded-full bg-matrix-400"
					initial={{
						x: Math.cos((i * Math.PI) / 4) * 100,
						y: Math.sin((i * Math.PI) / 4) * 100,
						opacity: 1,
					}}
					animate={{
						x: 0,
						y: 0,
						opacity: 0,
						scale: 0,
					}}
					transition={{
						duration: 1.5,
						repeat: Infinity,
						delay: i * 0.2,
						ease: 'easeIn',
					}}
				/>
			))}
		</div>
	);
}
